import React from "react";
import {
  Input,
  Select,
  Popover,
  Button,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import {
  UslLine,
  UseCase,
  SubscriptionProductLine,
} from "@amzn/fuse-accounting-central-datastore-client";
import { useValidationUtils } from "./ValidationUtils";

// Table Cell Component for USL Lines
interface TableCellProps {
  item: UslLine;
  fieldName: keyof UslLine;
  useCase: UseCase;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
}

export const TableCell: React.FC<TableCellProps> = ({
  item,
  fieldName,
  useCase,
  validationUtils,
  onFocus,
  onBlur,
}) => {
  const { getFieldTooltipContent, getFieldTextColor } = validationUtils;
  const index = useCase.uslLines?.indexOf(item) || 0;
  const tooltipContent = getFieldTooltipContent(fieldName, useCase.name, index);
  const textColor = getFieldTextColor(fieldName, useCase.name, index);
  const value = item[fieldName] || "";

  if (tooltipContent) {
    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="text"
        dismissButton={false}
        content={tooltipContent}
      >
        <span
          style={{
            cursor: "pointer",
            width: "100%",
            display: "block",
            color: textColor,
          }}
        >
          {value}
        </span>
      </Popover>
    );
  }

  return <span style={{ color: textColor }}>{value}</span>;
};

// Editing Cell Component for USL Lines
interface EditingCellProps {
  item: UslLine;
  fieldName: keyof UslLine;
  useCase: UseCase;
  currentValue: string;
  setValue: (value: string) => void;
  validationUtils: ReturnType<typeof useValidationUtils>;
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void;
  onBlur?: () => void;
}

export const EditingCell: React.FC<EditingCellProps> = ({
  item,
  fieldName,
  useCase,
  currentValue,
  setValue,
  validationUtils,
  onFocus,
  onBlur,
}) => {
  const { getFieldTooltipContent, getFieldValidationProps, getFieldErrors } =
    validationUtils;
  const index = useCase.uslLines?.indexOf(item) || 0;
  const tooltipContent = getFieldTooltipContent(fieldName, useCase.name, index);
  const validationProps = getFieldValidationProps(
    fieldName,
    useCase.name,
    index,
  );

  const handleFocus = () => {
    // Only trigger focus for derived attributes (red fields)
    const errors = getFieldErrors(fieldName, useCase.name, index);
    const hasErrorField = errors.some((error) =>
      error.derivedAttributes?.includes(fieldName),
    );

    if (hasErrorField && onFocus) {
      onFocus(fieldName, useCase.name, index);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const inputComponent = (
    <div onFocus={handleFocus} onBlur={handleBlur}>
      <Input
        autoFocus
        value={currentValue ?? item[fieldName] ?? ""}
        onChange={(e) => setValue(e.detail.value)}
        {...validationProps}
      />
    </div>
  );

  if (tooltipContent) {
    return (
      <Popover
        renderWithPortal
        size="large"
        position="top"
        triggerType="custom"
        dismissButton={false}
        content={tooltipContent}
      >
        {inputComponent}
      </Popover>
    );
  }

  return inputComponent;
};

// Table column definitions factory
export const createTableColumnDefinitions = (
  useCase: UseCase,
  useCaseIndex: number,
  validationUtils: ReturnType<typeof useValidationUtils>,
  handleDuplicateUslLine: (useCaseIndex: number, lineIndex: number) => void,
  handleDeleteUslLineRequest: (useCaseIndex: number, lineIndex: number) => void,
  onFocus?: (
    fieldName: string,
    useCaseName?: string | null,
    uslLineIndex?: number | null,
  ) => void,
  onBlur?: () => void,
) => {
  const uslLineFields: Array<{ id: keyof UslLine; header: string }> = [
    { id: "companyCode", header: "Company Code" },
    { id: "location", header: "Location" },
    { id: "costCenter", header: "Cost Center" },
    { id: "accountNumber", header: "Account Number" },
    { id: "productLine", header: "Product Line" },
    { id: "channel", header: "Channel" },
    { id: "project", header: "Project" },
    { id: "glLineDescriptionSuffix", header: "GL Line Description Suffix" },
  ];

  const standardColumns = uslLineFields.map(({ id, header }) => ({
    id,
    header,
    minWidth: 130,
    cell: (item: UslLine) => (
      <TableCell
        item={item}
        fieldName={id}
        useCase={useCase}
        validationUtils={validationUtils}
        onFocus={onFocus}
        onBlur={onBlur}
      />
    ),
    editConfig: {
      ariaLabel: header,
      editIconAriaLabel: `Edit ${header}`,
      editingCell: (item: UslLine, { currentValue, setValue }: any) => (
        <EditingCell
          item={item}
          fieldName={id}
          useCase={useCase}
          currentValue={currentValue}
          setValue={setValue}
          validationUtils={validationUtils}
          onFocus={onFocus}
          onBlur={onBlur}
        />
      ),
    },
  }));

  const debitCreditColumn = {
    id: "debitOrCredit",
    header: "Debit/Credit",
    cell: (item: UslLine) => item.debitOrCredit || "",
    editConfig: {
      ariaLabel: "Debit or Credit",
      editIconAriaLabel: "Edit Debit or Credit",
      editingCell: (item: UslLine, { currentValue, setValue }: any) => (
        <Select
          selectedOption={
            currentValue
              ? { label: currentValue, value: currentValue }
              : item.debitOrCredit
                ? { label: item.debitOrCredit, value: item.debitOrCredit }
                : null
          }
          onChange={({ detail }) =>
            setValue(detail.selectedOption?.value || "")
          }
          options={[
            { label: "Debit", value: "debit" },
            { label: "Credit", value: "credit" },
          ]}
          placeholder="Select debit or credit"
          expandToViewport
        />
      ),
    },
  };

  const subscriptionProductLineColumn = {
    id: "subscriptionProductLine",
    header: "Subscription Product Line",
    cell: (item: UslLine) => item.subscriptionProductLine || "",
    editConfig: {
      ariaLabel: "Subscription Product Line",
      editIconAriaLabel: "Edit Subscription Product Line",
      editingCell: (item: UslLine, { currentValue, setValue }: any) => (
        <Select
          selectedOption={
            currentValue
              ? { label: currentValue, value: currentValue }
              : item.subscriptionProductLine
                ? {
                    label: item.subscriptionProductLine,
                    value: item.subscriptionProductLine,
                  }
                : null
          }
          onChange={({ detail }) =>
            setValue(detail.selectedOption?.value || "")
          }
          options={Object.values(SubscriptionProductLine).map((v) => ({
            label: v,
            value: v,
          }))}
          placeholder="Select Subscription Product Line"
          expandToViewport
        />
      ),
    },
  };

  const actionsColumn = {
    id: "actions",
    header: "Actions",
    minWidth: 150,
    cell: (item: UslLine) => {
      const index = useCase.uslLines?.indexOf(item) || 0;
      return (
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="copy"
            variant="icon"
            onClick={() => handleDuplicateUslLine(useCaseIndex, index)}
            ariaLabel="Duplicate line"
          />
          <Button
            iconName="remove"
            variant="icon"
            onClick={() => handleDeleteUslLineRequest(useCaseIndex, index)}
            disabled={useCase.uslLines && useCase.uslLines.length <= 2}
            ariaLabel="Delete line"
          />
        </SpaceBetween>
      );
    },
  };

  return [
    ...standardColumns,
    debitCreditColumn,
    subscriptionProductLineColumn,
    actionsColumn,
  ];
};
